import { useContext } from "react";

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { add, isAfter, isBefore, sub } from "date-fns";
import { CalendarIcon, NetworkIcon } from "lucide-react";

import { formatDate, formatDateRelative } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { LabelItem } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { useThrottle } from "simplydo/react/hooks";
import { cx, styled } from "simplydo/react/styled";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Placeholder, PlaceholderImage } from "simplydo/react/ui/placeholder";
import { Popup } from "simplydo/react/ui/popup";
import type { UiColor } from "simplydo/react/ui/utils/colors";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas/useIdeaQueryParams";
import { IdeaBusinessProfileChip, UserChip } from "components/lib/Chips";
import IdeaDisplayFields from "components/lib/Ideas/IdeaDisplayFields";
import { Checkbox } from "components/lib/UI";

import { IdeaAssessmentProgress } from "../Ideas/IdeaAssessmentProgress";
import type { IIdeaCard } from "./Board";
import { LaneCardImpacts } from "./LaneCardImpacts";
import { ProjectBoardContext } from "./ProjectBoardContext";
import {
  type ProjectBoardEntry,
  type ProjectBoardIdea,
  type ProjectBoardUser,
} from "./types";

const StyledCard = styled.div`
  display: flex;
  flex: 1;
  cursor: pointer;
  flex-direction: column;
  gap: 4px;
  justify-content: space-between;
  box-sizing: border-box;
  overflow: visible !important;
  box-shadow:
    rgba(9, 30, 66, 0.25) 0px 1px 1px 0px,
    rgba(9, 30, 66, 0.31) 0px 0px 1px 0px;
  border-radius: 5px;
  padding: 8px 10px;
  margin: 2px 4px 0px 4px;
  background: white;

  &.is-highlighted {
    box-shadow:
      rgba(9, 30, 66, 0.25) 0px 1px 1px 0px,
      rgba(9, 30, 66, 0.31) 0px 0px 1px 0px,
      #fda590 0 0 0 2px inset;
  }

  &:not(.is-selecting):hover {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }

  &.is-selecting {
    cursor: default;

    &:not(:hover):not(.is-selected) {
      opacity: 0.6;
    }

    > *:not(.selection-box) {
      cursor: pointer;
    }
  }

  .selection-box {
    opacity: 1;
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
  }
  header {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  .card-header {
    line-height: 1.15em;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CardDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  .labels {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2px;
    margin-top: 4px;
    margin-bottom: 2px;
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }

  .more-labels {
    font-size: 12px;
    line-height: 10px;
    display: block;
    margin-left: 6px;
  }
`;

const MetaContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-top: 0.5px solid rgba(0, 0, 0, 0.2);
  padding-top: 5px;
  gap: 0;
`;

const AssigneeImage = styled.div<{
  $isCurrentUser?: boolean;
  $coverImage?: string;
}>`
  display: inline-block;
  width: ${({ $isCurrentUser }) => ($isCurrentUser ? "38px" : "25px")};
  height: ${({ $isCurrentUser }) => ($isCurrentUser ? "38px" : "25px")};
  border-radius: 50%;
  margin-right: -7px;
  box-shadow: 3px 3px 5px 0px rgba(0, 0, 0, 0.1);
  border: ${({ $isCurrentUser, theme }) =>
    $isCurrentUser ? `3px solid ${theme.primaryColour}` : "none"};
  background-size: cover;
  background-position: center center;
  background-image: ${({ $coverImage }) =>
    $coverImage ? `url(${$coverImage})` : "none"};

  &.show-initials {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f3f3f3;
    background-image: none;

    > span {
      display: block;
      color: #666666;
      font-weight: bold;
      text-align: center;
    }
  }
`;

const StyledStatusLabel = styled.div<{ $backgroundColor: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  line-height: 1.15em;
  > div {
    width: 8px;
    height: 8px;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border-radius: 8px;
  }
  > span {
    font-size: 11px;
  }
`;

const AssigneeImageContainer = styled.div<{ $count?: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;

  margin-right: ${({ $count = 0 }) => 7 + Math.min($count, 6) * 3}px;

  ${AssigneeImage} {
    margin-right: -${({ $count = 0 }) => 7 + Math.min($count, 6) * 3}px;
  }
`;

const getDeadlineStatus: (
  deadline: string | Date,
) =>
  | { color: UiColor; icon: IconData; text: string; isOverdue?: boolean }
  | undefined = (deadline) => {
  if (typeof deadline === "string") {
    deadline = new Date(deadline);
  }
  if (!deadline) return;
  if (deadline > add(new Date(), { days: 3 }))
    return {
      color: "green" as const,
      icon: CalendarIcon,
      text: "On track to deadline",
    };
  if (deadline < new Date()) {
    return {
      color: "red" as const,
      icon: CalendarIcon,
      text: "Deadline overdue",
      isOverdue: true,
    };
  }
  if (deadline > new Date())
    return {
      color: "orange" as const,
      icon: CalendarIcon,
      text: "Approaching deadline",
    };
};

const getReviewDateStatus: (
  reviewDate: string | Date,
) =>
  | { color: UiColor; icon: IconData; text: string; isOverdue?: boolean }
  | undefined = (reviewDate) => {
  if (typeof reviewDate === "string") {
    reviewDate = new Date(reviewDate);
  }
  if (!reviewDate) return;
  if (reviewDate > add(new Date(), { days: 3 }))
    return {
      color: "green" as const,
      icon: CalendarIcon,
      text: "On track to review date",
    };
  if (reviewDate < new Date()) {
    return {
      color: "red" as const,
      icon: CalendarIcon,
      text: "Review date overdue",
      isOverdue: true,
    };
  }
  if (reviewDate > new Date())
    return {
      color: "orange" as const,
      icon: CalendarIcon,
      text: "Approaching review date",
    };
};

const getWarning = (board?: ProjectBoardEntry) => {
  const updatedAt = board?.updatedAt && new Date(board.updatedAt);
  if (!updatedAt) {
    return { updatedAt: undefined, status: "green" };
  }

  const lowerLimit = sub(new Date(), { months: 1 });
  const upperLimit = sub(new Date(), { months: 3 });
  let status: string;
  if (isAfter(new Date(updatedAt), new Date(lowerLimit))) {
    status = "green";
  } else if (
    isBefore(new Date(updatedAt), new Date(lowerLimit)) &&
    isAfter(new Date(updatedAt), new Date(upperLimit))
  ) {
    status = "orange";
  } else {
    status = "red";
  }
  return { updatedAt, status };
};

const clickEventTargetIsModal = (target: HTMLElement) => {
  const clases = target.classList;
  if (
    clases.contains("popup") ||
    clases.contains("modal") ||
    clases.contains("chip") ||
    clases.contains("button")
  ) {
    return true;
  }

  if (!target.parentElement) {
    return false;
  }

  return clickEventTargetIsModal(target.parentElement);
};

type CustomLaneCardProps = { card: IIdeaCard };

const CustomLaneCard = ({ card }: CustomLaneCardProps) => {
  const { isSelecting, selectedIdeas, selectIdeas, loading, setPreviewId } =
    useContext(ProjectBoardContext);

  const isSelected =
    selectedIdeas.find((i) => i._id === card.idea._id) !== undefined;

  const user: ProjectBoardUser = useAppSelector((state) => state.user);

  const { idea, laneMetaType } = card;
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const highlightCardId = searchParams.get("focus");

  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [idea.challenge], {
      select: (data) => ({ impactStatements: data.impactStatements }),
    }),
  );

  const ideaViewType = useAppSelector((state) => state.challenges.ideaViewType);
  const queryParams = useIdeaQueryParams(ideaViewType);
  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [idea.challenge, queryParams], {
      select: (data) => data.ideaImpacts?.[idea._id],
    }),
  );

  if (!idea || !idea?._id) return null;
  const board = idea?.projectManagement?.boards?.find(
    (b) => b.forId === idea.challenge,
  ) as ProjectBoardEntry | undefined;
  const deadlineStatus = board?.deadline
    ? getDeadlineStatus(board.deadline)
    : undefined;
  const reviewDateStatus = board?.reviewDate
    ? getReviewDateStatus(board.reviewDate)
    : undefined;
  const { updatedAt, status } = getWarning(board);

  const cardLaneHasMetaType =
    laneMetaType &&
    (laneMetaType === "notProgressing" ||
      laneMetaType === "completed" ||
      laneMetaType === "resolved");

  const allImpacts = ideaImpactsQuery.data?.totalImpacts ?? [];
  const displayFields = (
    (idea as ProjectBoardIdea).ownerIdeaTemplate?.body
      ?.filter((section) => section?.type !== "assessment")
      .flatMap((section) =>
        section.fields.filter(
          (field) => field.isDisplayField && idea.templated?.[field.id],
        ),
      ) || []
  ).map((field) => ({
    id: field.id,
    value: idea.templated?.[field.id],
    title: field.title,
    type: field.type,
  }));

  return (
    <StyledCard
      className={cx(
        isSelecting && "is-selecting",
        isSelected && "is-selected",
        highlightCardId === idea._id && "is-highlighted",
      )}
      onClick={(e) => {
        if (isSelecting) {
          selectIdeas([idea], !isSelected);
        } else {
          if (clickEventTargetIsModal(e.target as HTMLElement)) {
            return;
          }
          setPreviewId(idea._id);
        }
      }}
    >
      <div>
        {isSelecting ? (
          <Checkbox
            className="selection-box"
            isSelected={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              selectIdeas([idea], !isSelected);
            }}
          />
        ) : null}
        <header>
          <CardHeader>
            <Popup
              trigger={
                <b className="card-header" style={{ margin: 0 }}>
                  {idea.name}
                </b>
              }
              disabled={!!idea.name && idea.name.length <= 70}
            >
              {idea.name}
            </Popup>
            {idea.children ? (
              <Popup
                trigger={
                  <Icon
                    icon={NetworkIcon}
                    size="small"
                    style={{ marginTop: 2 }}
                  />
                }
              >{`This ${t("generic.idea")} is a merged ${t("generic.idea")}.`}</Popup>
            ) : null}
          </CardHeader>
        </header>
        <CardDetails onDragStart={(e) => e.preventDefault()}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {idea.labels?.length ? (
              <div className="labels">
                {idea.labels.slice(0, 5).map((label) => (
                  <div key={label} className="label-chip">
                    <LabelItem label={label} />
                  </div>
                ))}
                {idea.labels?.length > 5 ? (
                  <Popup
                    trigger={
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <LabelItem
                          label={`+ ${idea.labels?.length - 5} more`}
                        />
                      </div>
                    }
                  >
                    {
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        {idea.labels.slice(5).map((label) => (
                          <div key={label}>
                            <LabelItem label={label} />
                          </div>
                        ))}
                      </div>
                    }
                  </Popup>
                ) : null}
              </div>
            ) : null}
            {!idea.isSubmitted ? (
              <StyledStatusLabel $backgroundColor="orange">
                <div />
                <span>Draft {t("generic.idea")}</span>
              </StyledStatusLabel>
            ) : null}
            {updatedAt ? (
              <Popup
                position="right center"
                trigger={
                  <StyledStatusLabel $backgroundColor={status}>
                    <div />
                    <span>Updated: {formatDate(updatedAt, "do MMM ''yy")}</span>
                  </StyledStatusLabel>
                }
              >{`This ${t("generic.idea")} was last updated on ${formatDate(updatedAt, "dd/MM/yyyy")} (${formatDateRelative(updatedAt, { addSuffix: true })})`}</Popup>
            ) : null}
            {deadlineStatus && board?.deadline && !cardLaneHasMetaType ? (
              <Popup
                position="right center"
                trigger={
                  <StyledStatusLabel $backgroundColor={deadlineStatus.color}>
                    <div />
                    <span>
                      Deadline: {formatDate(board.deadline, "do MMM ''yy")}
                    </span>
                  </StyledStatusLabel>
                }
              >{`${deadlineStatus.text} : ${formatDate(board.deadline, "dd/MM/yyyy")} (${formatDateRelative(board.deadline, { addSuffix: true })})`}</Popup>
            ) : null}
            {reviewDateStatus && board?.reviewDate && !cardLaneHasMetaType ? (
              <Popup
                position="right center"
                trigger={
                  <StyledStatusLabel $backgroundColor={reviewDateStatus.color}>
                    <div />
                    <span>
                      Review date: {formatDate(board.reviewDate, "do MMM ''yy")}
                    </span>
                  </StyledStatusLabel>
                }
              >{`${reviewDateStatus.text} : ${formatDate(board.reviewDate, "dd/MM/yyyy")} (${formatDateRelative(board.reviewDate, { addSuffix: true })})`}</Popup>
            ) : null}
            {idea.assessments?.length ? (
              loading ? (
                "Loading assessments..."
              ) : (
                <IdeaAssessmentProgress idea={idea} compact={true} />
              )
            ) : null}
          </div>
        </CardDetails>
        {allImpacts.length !== 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              border: "1px solid #eee",
              padding: 5,
              borderRadius: 4,
              gap: 6,
              marginTop: 4,
            }}
          >
            <LaneCardImpacts
              showUserEstimate={
                !!challengeQuery.data?.impactStatements?.showOnIdea
              }
              impactData={ideaImpactsQuery.data}
            />
          </div>
        ) : null}
        {displayFields.length > 0 ? (
          <div style={{ marginTop: 3 }}>
            <IdeaDisplayFields fields={displayFields} />
          </div>
        ) : null}
      </div>
      {idea.lockedOwnerIdeaBusinessProfile ||
      (idea as ProjectBoardIdea).owner ? (
        <MetaContainer>
          {idea.lockedOwnerIdeaBusinessProfile ||
          (idea as ProjectBoardIdea).owner ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginRight: 5,
                minWidth: 0,
              }}
            >
              <span
                style={{ fontSize: 10, lineHeight: "13px", color: "#7a7a7a" }}
              >
                Created by
              </span>
              <span
                style={{ fontSize: 12, lineHeight: "14px", color: "#7a7a7a" }}
              >
                {idea.lockedOwnerIdeaBusinessProfile ? (
                  <IdeaBusinessProfileChip
                    compact="very"
                    ideaBusinessProfile={idea.lockedOwnerIdeaBusinessProfile}
                  />
                ) : (
                  <>
                    {loading ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Placeholder
                          style={{ height: 30, width: 30, borderRadius: 5 }}
                        >
                          <PlaceholderImage square />
                        </Placeholder>
                      </div>
                    ) : null}
                    {(idea as ProjectBoardIdea).owner ? (
                      <UserChip
                        user={(idea as ProjectBoardIdea).owner}
                        compact="very"
                      />
                    ) : null}
                  </>
                )}
              </span>
            </div>
          ) : (
            <div />
          )}
          <AssigneeImageContainer $count={board?.assigneeUsers?.length ?? 0}>
            {(board?.assigneeUsers?.length ?? 0) > 0 ? (
              <>
                {(board?.assigneeUsers ?? []).slice(0, 6).map((assignee) => {
                  const showInitials =
                    !assignee?.profile?.avatar &&
                    !!assignee.profile?.firstName &&
                    !!assignee.profile?.lastName;
                  const coverImage = showInitials
                    ? undefined
                    : util.mixinCssUrlFallback(
                        util.avatarUrl(assignee),
                        util.avatarUrl(),
                      );

                  return (
                    <Popup
                      key={assignee._id}
                      trigger={
                        <AssigneeImage
                          key={assignee._id}
                          className={cx(showInitials && "show-initials")}
                          $coverImage={coverImage}
                          $isCurrentUser={assignee._id === user?._id}
                        >
                          {showInitials && (
                            <span>
                              {assignee.profile.firstName[0]?.toUpperCase()}
                              {assignee.profile.lastName[0]?.toUpperCase()}
                            </span>
                          )}
                        </AssigneeImage>
                      }
                    >
                      {assignee._id === user?._id
                        ? `You are assigned to this ${t("generic.idea")}.`
                        : `${assignee.profile?.fullName ?? "User"} is assigned to this ${t("generic.idea")}.`}
                    </Popup>
                  );
                })}
                {(board?.assigneeUsers?.length ?? 0) > 6 ? (
                  <Popup
                    trigger={
                      <AssigneeImage className="show-initials">
                        <span>{`+${(board?.assigneeUsers?.length ?? 0) - 6}`}</span>
                      </AssigneeImage>
                    }
                  >
                    {(board?.assigneeUsers ?? []).slice(6).map((a) => (
                      <p key={a._id}>{a.profile.fullName}</p>
                    ))}
                  </Popup>
                ) : null}
              </>
            ) : null}
          </AssigneeImageContainer>
        </MetaContainer>
      ) : null}
    </StyledCard>
  );
};

export default CustomLaneCard;
